import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../dataManagement/uiDb/UiDb";
import BusinessConfigApiService from "../apiServices/BusinessConfigApiService";
import { BusinessConfigData } from "../../interfaces/BusinessObject";

function rejection(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Full config for a business — public endpoint, no auth needed. Callers
 * should check `state.businessConfig.configByBusinessId[businessId]` first
 * and only dispatch when it's missing/stale (same convention as
 * fetchSectionTree). `condition` skips dispatching entirely while a fetch
 * for the same businessId is already in flight, so two mounting consumers
 * (e.g. the business page and the config editor) don't double-fetch.
 */
export const fetchBusinessConfig = createAsyncThunk(
  "businessConfig/fetchBusinessConfig",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const config = await BusinessConfigApiService.getBusinessConfig(businessId);
      return { businessId, config };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  },
  {
    condition: (businessId, { getState }) => {
      const state = getState() as RootState;
      return !state.businessConfig.loadingMap[businessId];
    },
  }
);

export interface UpdateBusinessConfigThunkParams {
  businessId: string;
  fields: Partial<BusinessConfigData>;
}

/**
 * Partial update — only `fields`' keys are written (and only those columns
 * touched server-side). Resolves to the server's full post-save config, so
 * the cache is replaced wholesale rather than merged client-side.
 */
export const updateBusinessConfig = createAsyncThunk(
  "businessConfig/updateBusinessConfig",
  async (params: UpdateBusinessConfigThunkParams, { rejectWithValue }) => {
    try {
      const config = await BusinessConfigApiService.updateBusinessConfig(
        params.businessId,
        params.fields
      );
      return { businessId: params.businessId, config };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

/** Soft-disables every field (is_enabled=false) without clearing stored
 *  values. Resolves to the server's full post-disable config. */
export const disableBusinessConfig = createAsyncThunk(
  "businessConfig/disableBusinessConfig",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const config = await BusinessConfigApiService.disableBusinessConfig(businessId);
      return { businessId, config };
    } catch (error) {
      return rejectWithValue(rejection(error));
    }
  }
);

const BusinessConfigThunks = {
  fetchBusinessConfig,
  updateBusinessConfig,
  disableBusinessConfig,
};

export default BusinessConfigThunks;
